import { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { PricingHero } from '../ui/pricing-hero';
import { BillingToggle } from '../ui/billing-toggle';
import { PricingCard } from '../ui/pricing-card';
import { BillingCycle, PricingData, PricingTier } from '../data/interfaces/pricing-types';
import { withErrorHandling } from '@selfserve/react-hocs';
import {
  ApplicationFeatures, ButtonType,
  PageType,
  responseTypes,
  routesPaths,
  subscriptionTypes,
  trackButtonClickEvent,
  trackPageLoadEvent
} from '@selfserve/react-utils';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLoginForUpgrade } from '../index';
import { usePackageByProductFamilyRequest } from '@selfserve/react-common-data';
import { Box, CircularProgress } from '@mui/material';
import { postMessageToParent, useKsFromHost, useSubscriptionOverviewRequest } from '@selfserve/react-common-data';
import { storageKeys, useAuth, useQueryParams } from '@selfserve/react-hooks';

const StyledPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  background: white;
`;

const StyledContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
  align-items: center;
  padding: 32px;
  width: 100%;
`;

const StyledCardsGrid = styled.div`
  display: flex;
  gap: 32px;
  align-items: flex-start;
  justify-content: center;
  flex-wrap: wrap;
  max-width: 1440px;
`;

const PricingPageComponent = () => {
  const [selectedCycle, setSelectedCycle] = useState<BillingCycle>(BillingCycle.ANNUAL);
  const [pricingData, setPricingData] = useState<PricingData | null>(null);
  const [currentPackage, setCurrentPackage] = useState<PricingTier | null>(null);
  const [hasProductFamilySubscription, setHasProductFamilySubscription] = useState(false);
  const [loginCompleted, setLoginCompleted] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { productFamily } = useParams();
  const queryParams = useQueryParams();
  const { mutate: loginForUpgrade } = useLoginForUpgrade();
  // wait for the ks only when opened by a unisphere-plg runtime - plans iframe (inAppContext) or widgets safari tab (ksFromOpener)
  const { ks, isResolved: isKsResolved } = useKsFromHost(
    'unisphere.widget.plg.events.upgrade-flow-ks-requested',
    queryParams['inAppContext'] === 'true' || queryParams['ksFromOpener'] === 'true'
  );

  const { hasAccessToken } = useAuth();

  const shouldLogin = useMemo(() => !!(productFamily && ks), [productFamily, ks]);
  // Without a ks - use an existing access_token JWT only if there is a known session; anonymous users skip the subscription request
  const shouldUseExistingToken = !shouldLogin && hasAccessToken;

  // With a ks - enable subscription request only after login completes
  const {
    data: subscriptionOverviewData,
    isFetched: subscriptionOverviewFetched
  } = useSubscriptionOverviewRequest(isKsResolved && (shouldLogin ? loginCompleted : shouldUseExistingToken));

  useEffect(() => {
    const isInAppContext = queryParams['inAppContext'] === 'true';
    if (isInAppContext) {
      sessionStorage.setItem(storageKeys.IS_IN_APP_CONTEXT, 'true');
    }

    const hostingKalturaApplication = queryParams['hostingKalturaApplication'];
    if (hostingKalturaApplication) {
      sessionStorage.setItem('hostingKalturaApplication', hostingKalturaApplication);
    } else {
      // Clean up any previous value to avoid incorrect assumptions in future visits without the parameter
      sessionStorage.removeItem('hostingKalturaApplication');
    }

    postMessageToParent({
      id: 'unisphere.services.iframes.emit',
      version: '1.0.0',
      payload: {
        id: 'unisphere.widget.plg.events.upgrade-flow-change-page',
        version: '1.0.0',
        payload: {
          isInitial: true // Let the parent know the initial page of the upgrade flow was loaded
        },
      }
    });

    const handleBeforeUnload = () => {
      postMessageToParent({
        id: 'unisphere.services.iframes.emit',
        version: '1.0.0',
        payload: {
          id: 'unisphere.widget.plg.events.upgrade-flow-change-page',
          version: '1.0.0',
          payload: {
            isInitial: false // Let the parent know the user is navigating away
          },
        }
      });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    if (!shouldLogin) return;

    loginForUpgrade({ productFamily: productFamily || '', ks: ks || '' }, {
      onSuccess: (res) => {
        const { status } = res;
        if (status >= 200 && status < 300) {
          trackPageLoadEvent(
            PageType.View,
            'pricing_page',
            ApplicationFeatures.SUBSCRIPTION_FLOW,
            productFamily
          );
          setLoginCompleted(true);
        } else {
          console.warn('LoginForUpgrade request returned non-success status:', status);
        }
      },
      onError: () => {
        console.error('Login for upgrade request failed');
      }
    });
  }, [shouldLogin]);

  if (!productFamily) {
    navigate(routesPaths.LOGIN);
    return null;
  }

  const {
    data: packageData,
    isError: packageResponseError,
    remove: packageRequestRemove,
    isLoading: packageRequestLoading
  } = usePackageByProductFamilyRequest(productFamily);

  useEffect(() => {
    // wait for the host to provide the ks - then wait for the login to complete
    if (
      !isKsResolved ||
      (shouldLogin && !subscriptionOverviewData?.data) ||
      (shouldUseExistingToken && !subscriptionOverviewFetched)
    ) {
      return;
    }
    if (!packageResponseError && String(packageData?.status).startsWith(responseTypes.SUCCESS)) {
      let tiers = packageData?.data?.map(pkg => {
        if (pkg.custom_data?.packageDisplayInfo) {
          const { packageDisplayInfo } = pkg.custom_data;
          return {
            ...packageDisplayInfo,
            id: pkg.name,
            type: pkg.type
          }
        }
        return null;
      }).filter(pkg => pkg !== null);
      // Sort tiers by price
      if (tiers) {
        tiers = tiers.sort((a, b) => {
          return a.level - b.level;
        });
      }
      setPricingData({ tiers: tiers || [] });

      // Find the user's subscription of this product family
      if (tiers?.length) {
        // ignore the cached subscription overview once the session ended (logged out in another tab)
        const subscriptions = (shouldLogin || shouldUseExistingToken)
          ? subscriptionOverviewData?.data?.subscriptionInfoList || []
          : [];
        const productFamilySubscription = subscriptions.find(sub => sub.planItem?.product_family === productFamily);
        // set the current package (logged in with ks or with an existing JWT)
        const currentPkg = tiers.find(tier => tier.id === productFamilySubscription?.planItem?.name);
        setCurrentPackage(currentPkg || null);
        if (!shouldLogin) {
          // no ks - keep showing all tiers; block the free tier only if logged in with an existing JWT and already subscribed to this product family
          setHasProductFamilySubscription(!!productFamilySubscription);
        }
      }
    } else if (packageResponseError || packageData?.status) {
      navigate(routesPaths.LOGIN);
      packageRequestRemove();
    }
  }, [packageData, subscriptionOverviewData, subscriptionOverviewFetched, shouldLogin, shouldUseExistingToken, isKsResolved]);

  return (
    <StyledPageContainer>
      {(!isKsResolved || packageRequestLoading || (shouldUseExistingToken && !subscriptionOverviewFetched)) && <Loader />}
      {pricingData && (
        <>
          {/*<PricingHeader />*/}
          <StyledContentContainer>
            <PricingHero product={productFamily} />
            <BillingToggle selected={selectedCycle} onChange={(billingCycle: BillingCycle) => {
              setSelectedCycle(billingCycle);
              trackButtonClickEvent(
                ButtonType.Toggle,
                'pricing_page_billing_cycle',
                billingCycle,
                null,
                ApplicationFeatures.SUBSCRIPTION_FLOW
              );
            }}/>
            <StyledCardsGrid>
              {pricingData.tiers.map((tier) => (
                // Show all tiers if there is no ks (existing JWT flow) or when user has no current package
                // otherwise show tiers that are the same level or higher than the user's current package AND any paid tiers (to allow users to downgrade to lower paid tiers)
                (!shouldLogin || !currentPackage || tier.level >= currentPackage.level || tier.type === subscriptionTypes.PAID) && <PricingCard
                  key={tier.id}
                  tier={tier}
                  billingCycle={selectedCycle}
                  isCurrent={currentPackage?.id === tier.id}
                  disabled={hasProductFamilySubscription && tier.type === subscriptionTypes.FREE}
                  disabledTooltip={t('pricing-page.new-avatar-studio-customers-only')}
                />
              ))}
            </StyledCardsGrid>
          </StyledContentContainer>
        </>
      )}
    </StyledPageContainer>
  );
};

const Loader = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100vh',
      }}
    >
      <CircularProgress size={48} />
    </Box>
  );
}

export const PricingPage = withErrorHandling(PricingPageComponent);
